import { formatPhone, PhoneField } from '@flos/react-ui';
import { useEffect, useState } from 'react';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { isRequired } from '../../helpers/isRequired';
import { SummaryRowTemplate } from '../../helpers/templates';
import { getPrimaryPhone } from '../../helpers/topContext';
import useAppState from '../../state/appState';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { ElementValue, ElementValueHuman } from '../../types/element-values';
import { ContactPhone as ContactPhoneType, FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { getValue } from '../values/Get';

const ContactPhone = ({ caption, placeholder, elementId }: ContactPhoneType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const valueIsDefinedInState = formValues[elementId] === undefined ? false : true;

    const value = ContactPhone.getValueOrDefault(formValues, elementId);

    useEffect(() => {
        if (!valueIsDefinedInState) {
            setFormValue(elementId, value);
        }
    }, [valueIsDefinedInState, setFormValue, elementId, value]);

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    const setValue = (value: string) => {
        setFormValue(elementId, value);
    };

    const isRequiredField = isRequired(formConfig, elementId);
    const isValid = ContactPhone.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <PhoneField
                value={value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    setValue(value);
                }}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                errorText={errorMessage}
                isValid={isValid}
                id={elementId}
                key={elementId}
                label={placeholder}
                required={isRequiredField}
                aria-label={caption}
                data-testid="contact-phone"
            />
        </>
    );
};

ContactPhone.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as ContactPhoneType;
    const { summaryCaption } = element;
    const value = getValue(formConfig, formValues, elementId);
    const formattedValue = formatPhone(value) as string;
    if (!formattedValue) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={formattedValue} />;
};

ContactPhone.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    caption: string
): ClaimFormSubmitFieldValue | undefined => {
    // This contact information is handled in a separate object in the payload Stakeholder and Actions
    return undefined;
};

ContactPhone.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    const value = formValues[elementId];
    return value;
};

ContactPhone.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = ContactPhone.getValue(formValues, elementId);

    if (value === undefined) {
        return getPrimaryPhone();
    }
    return value;
};

ContactPhone.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

ContactPhone.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }
    const isValid = ContactPhone.isValid(formConfig, formValues, elementId);
    return isValid;
};

ContactPhone.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = ContactPhone.getValue(formValues, elementId);
    return value;
};

export default ContactPhone;
